import { useState, useEffect, useMemo, useRef } from 'react';
import {
  deleteOrgFile,
  listOrgCollection,
  readOrgFile,
  writeBidirectional,
  writeOrgFile,
} from '../../storage/FileSystemAdapter';
import { BG_CONTENT, BG_SUBTLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_DESTRUCTIVE_FILLED, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_BAD, STATUS_WARN, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_BUTTON, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_STAT_VALUE } from '../../theme';
import { dateStatus } from '../../utils/dateStatus';
import { callWorker } from '../../utils/workerClient';
import { appendChangeLog, isRecordImmutable } from '../../utils/changeLog';
import { scanReferencesToEntity } from '../../utils/intelligence';
import { maybeAdvancePdcaStage } from '../../utils/pdcaStage';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import AISuggestButton from '../shared/AISuggestButton';
import TagEditor from '../shared/TagEditor';
import TagInput from '../shared/TagInput';
import EntitySelector from '../shared/EntitySelector';
import SOA_CONTROLS from '../../data/soa-controls';
import { ISO_CLAUSES } from '../../iso-clauses';
import ConfirmDialog from '../shared/ConfirmDialog';
import { AUDIT_STATUSES } from '../../utils/enums';
import useScrollToTopOnFormOpen from '../../utils/useScrollToTopOnFormOpen';

// ─── §11 RULE 42 — BLOCK DIALOG SUPPORT ──────────────────────────────────────
const HOLDER_NAV = {
  'risk': 'risks', 'asset': 'assets', 'supplier': 'suppliers',
  'incident': 'incidents', 'audit': 'audits', 'nonconformity': 'nonconformities',
  'objective': 'objectives', 'metric': 'metrics', 'document': 'documents',
  'legal-register': 'legal-register', 'management-review': 'management-reviews',
  'soa-control': 'soa', 'scope': 'scope',
};
function holderLabel(t, holderType, count) {
  const key = `deleteBlockedHolder_${holderType}_${count === 1 ? 'one' : 'other'}`;
  return t[key] || holderType;
}
function summariseHits(hits, t) {
  const counts = {};
  hits.forEach(h => { counts[h.holderType] = (counts[h.holderType] || 0) + 1; });
  return Object.entries(counts).map(([type, n]) => `${n} ${holderLabel(t, type, n)}`).join(', ');
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const AUDIT_TYPES    = ['Internal', 'Stage1', 'Stage2', 'Surveillance', 'Recertification'];
const AUDIT_METHODS  = ['Interview', 'Observation', 'Document review', 'Sampling', 'Testing'];
const FINDING_TYPES  = ['Major NC', 'Minor NC', 'Observation', 'Opportunity for Improvement'];
const PRIORITIES     = ['High', 'Medium', 'Low'];

const TYPE_ABBREV = {
  Internal:        'INT',
  Stage1:          'ST1',
  Stage2:          'ST2',
  Surveillance:    'SUR',
  Recertification: 'REC',
};

// ── COLORS ────────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'Planned':     { bg: '#e0e7ef', text: TEXT_DEFAULT },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Completed':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const FINDING_TYPE_COLORS = {
  'Major NC':                    { bg: ERROR_BG, text: ERROR_TEXT },
  'Minor NC':                    { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Observation':                 { bg: '#e0e7ef', text: TEXT_DEFAULT },
  'Opportunity for Improvement': { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

const PRIORITY_COLORS = {
  'High':   { bg: ERROR_BG, text: ERROR_TEXT },
  'Medium': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Low':    { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:                   'Auditoría interna',
    newAudit:                '+ Nueva auditoría',
    save:                    'Guardar',
    saving:                  'Guardando…',
    saveOk:                  '✓ Guardado',
    saveError:               'Error al guardar',
    unsavedLabel:            'Cambios sin guardar',
    unsavedConfirm:          'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:      'Salir sin guardar',
    saveAndLeave:            'Guardar y salir',
    loading:                 'Cargando…',
    cntTotal:                'Total',
    cntPlanned:              'Planificadas',
    cntInProgress:           'En progreso',
    cntCompleted:            'Completadas',
    viewBtn:                 'Ver',
    editBtn:                 'Editar',
    deleteBtn:               'Eliminar',
    deleteFormBtn:           'Eliminar auditoría',
    deleteConfirm:           '¿Eliminar la auditoría "{name}"? Esta acción no se puede deshacer.',
    selectAll:               'Seleccionar todo',
    nSelected:               '{n} seleccionados',
    deleteSelected:          'Eliminar seleccionados',
    deleteSelectedConfirm:   '¿Eliminar las siguientes auditorías?\n\n{names}\n\nEsta acción no se puede deshacer.',
    emptyTitle:              'No hay auditorías registradas.',
    emptyHint:               'Crea la primera auditoría con el botón "+ Nueva auditoría".',
    formTitleNew:            'Nueva auditoría',
    formTitleEdit:           'Editar auditoría',
    formTitleView:           'Ver auditoría',
    backToList:              '← Volver al listado',
    sectionA:                'Detalles de la auditoría',
    sectionB:                'Hallazgos',
    sectionRel:              'Relaciones',
    fRef:                    'Referencia',
    fType:                   'Tipo',
    fAuditor:                'Auditor',
    fProgrammeYear:          'Año del programa de auditoría',
    fIndependence:           'Auditor independiente',
    fMethods:                'Métodos de auditoría',
    fPlannedDate:            'Fecha planificada',
    fActualDate:             'Fecha real',
    fScope:                  'Alcance',
    fCriteria:               'Criterios',
    fStatus:                 'Estado',
    fReportPath:             'Ruta del informe',
    fReportPathPh:           'p. ej. audits/informe-2026.pdf',
    fEvidenceIds:            'IDs de evidencias',
    evidencePlaceholder:     'Introducir ID de evidencia y pulsar Enter…',
    fTags:                   'Etiquetas',
    fFindingEvidenceIds:     'IDs de evidencias',
    methodLabels:            {
      'Interview':       'Entrevista',
      'Observation':     'Observación',
      'Document review': 'Revisión documental',
      'Sampling':        'Muestreo',
      'Testing':         'Pruebas',
    },
    tagPlaceholder:          'Añadir y pulsar Enter…',
    findingsHeader:          'hallazgos',
    findingColId:            'ID',
    findingColType:          'Tipo',
    findingColClause:        'Cláusula',
    findingColControl:       'Control',
    findingColDesc:          'Descripción',
    findingColRisk:          'Impacto',
    findingColPriority:      'Prioridad',
    findingColRec:           'Recomendación',
    findingColNcRef:         'Ref. NC',
    findingColActions:       'Acciones',
    findingType:             'Tipo',
    findingClause:           'Cláusula',
    findingControl:          'Control del Anexo A',
    findingDesc:             'Descripción',
    findingRisk:             'Impacto del riesgo',
    findingPriority:         'Prioridad',
    findingRec:              'Recomendación',
    createNcBtn:             'Crear NC',
    createNcTitle:           'Crear no conformidad desde hallazgo',
    createNcOwnerLabel:      'Responsable *',
    createNcConfirm:         'Crear NC',
    createNcCancel:          'Cancelar',
    createNcUnsaved:         'Hay cambios sin guardar. ¿Continuar sin guardar y crear la NC?',
    ncBadgePrefix:           'NC:',
    addFinding:              'Añadir hallazgo',
    showFindingForm:         '+ Añadir hallazgo',
    hideFindingForm:         '− Cerrar',
    deleteFindingConfirm:    '¿Eliminar este hallazgo?',
    findingsSummaryNone:     'Sin hallazgos',
    findingValidationError:  'Completa los campos requeridos del hallazgo antes de añadirlo.',
    findingClausePh:         'Buscar cláusula…',
    findingControlPh:        'Buscar control del Anexo A…',
    clauseOrControlRequired: 'Debes indicar al menos una cláusula o un control del Anexo A.',
    formSave:                'Guardar auditoría',
    formCancel:              'Cancelar',
    validationError:         'Por favor completa los campos obligatorios marcados con *.',
    aiError:                 'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:       'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired:       'Activa el procesamiento con IA en Configuración para usar esta función.',
    colRef:                  'Referencia',
    colType:                 'Tipo',
    colStatus:               'Estado',
    colAuditor:              'Auditor',
    colPlannedDate:          'Fecha planificada',
    colFindings:             'Hallazgos',
    colActions:              'Acciones',
    typeLabels: {
      Internal:        'Interna',
      Stage1:          'Fase 1',
      Stage2:          'Fase 2',
      Surveillance:    'Seguimiento',
      Recertification: 'Recertificación',
    },
    statusLabels: {
      'Planned':     'Planificada',
      'In Progress': 'En curso',
      'Completed':   'Completada',
    },
    findingLabels: {
      'Major NC':                    'NC Mayor',
      'Minor NC':                    'NC Menor',
      'Observation':                 'Observación',
      'Opportunity for Improvement': 'Oportunidad de mejora',
    },
    priorityLabels: {
      High:   'Alta',
      Medium: 'Media',
      Low:    'Baja',
    },
    filterType:         'Tipo',
    filterStatus:       'Estado',
    filterFindings:     'Hallazgo',
    filterAll:          'Todos',
    filterNoFindings:   'Sin hallazgos',
    filterNSelected:    '{n} seleccionados',
    filterNoSelection:  'Todos',
    required:           '*',
    requiredLegend:     '* Campos obligatorios',
    frozenBanner:       '🔒 Este registro es de solo lectura. Para modificarlo, reabre desde Ajustes → Reabrir registros cerrados.',
    badgeOverdue:       'Vencido',
    badgeSoon:          'Vence pronto',
    deleteBlockedTitle:        'No se puede eliminar: existen referencias',
    deleteBlockedReferencedBy: 'Referenciado por',
    deleteBlockedOpenHolder:   'Abrir',
    deactivateInstead:         'Desactivar en su lugar',
    bulkDeleteSummary_allBlocked: 'Ninguno de los registros seleccionados puede eliminarse porque todos están referenciados por otros registros.',
    bulkDeleteSummary_mixed:      '{deletable} de {total} registros pueden eliminarse; {blocked} están bloqueados por referencias.',
    continueWithDeletables:       'Eliminar los registros no bloqueados',
    deleteBlockedHolder_risk_one:               'riesgo',
    deleteBlockedHolder_risk_other:             'riesgos',
    deleteBlockedHolder_asset_one:              'activo',
    deleteBlockedHolder_asset_other:            'activos',
    deleteBlockedHolder_supplier_one:           'proveedor',
    deleteBlockedHolder_supplier_other:         'proveedores',
    deleteBlockedHolder_incident_one:           'incidente',
    deleteBlockedHolder_incident_other:         'incidentes',
    deleteBlockedHolder_audit_one:              'auditoría',
    deleteBlockedHolder_audit_other:            'auditorías',
    deleteBlockedHolder_nonconformity_one:      'no conformidad',
    deleteBlockedHolder_nonconformity_other:    'no conformidades',
    deleteBlockedHolder_objective_one:          'objetivo',
    deleteBlockedHolder_objective_other:        'objetivos',
    deleteBlockedHolder_metric_one:             'métrica',
    deleteBlockedHolder_metric_other:           'métricas',
    deleteBlockedHolder_document_one:           'documento',
    deleteBlockedHolder_document_other:         'documentos',
    'deleteBlockedHolder_legal-register_one':   'entrada del registro legal',
    'deleteBlockedHolder_legal-register_other': 'entradas del registro legal',
    'deleteBlockedHolder_management-review_one':   'revisión por la dirección',
    'deleteBlockedHolder_management-review_other': 'revisiones por la dirección',
    'deleteBlockedHolder_soa-control_one':      'control del SoA',
    'deleteBlockedHolder_soa-control_other':    'controles del SoA',
    deleteBlockedHolder_scope_one:              'alcance',
    deleteBlockedHolder_scope_other:            'alcance',
  },
  en: {
    title:                   'Internal audit',
    newAudit:                '+ New audit',
    save:                    'Save',
    saving:                  'Saving…',
    saveOk:                  '✓ Saved',
    saveError:               'Save error',
    unsavedLabel:            'Unsaved changes',
    unsavedConfirm:          'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:      'Leave without saving',
    saveAndLeave:            'Save and leave',
    loading:                 'Loading…',
    cntTotal:                'Total',
    cntPlanned:              'Planned',
    cntInProgress:           'In Progress',
    cntCompleted:            'Completed',
    viewBtn:                 'View',
    editBtn:                 'Edit',
    deleteBtn:               'Delete',
    deleteFormBtn:           'Delete audit',
    deleteConfirm:           'Delete audit "{name}"? This cannot be undone.',
    selectAll:               'Select all',
    nSelected:               '{n} selected',
    deleteSelected:          'Delete selected',
    deleteSelectedConfirm:   'Delete the following audits?\n\n{names}\n\nThis cannot be undone.',
    emptyTitle:              'No audits recorded.',
    emptyHint:               'Create the first audit using the "+ New audit" button.',
    formTitleNew:            'New audit',
    formTitleEdit:           'Edit audit',
    formTitleView:           'View audit',
    backToList:              '← Back to list',
    sectionA:                'Audit details',
    sectionB:                'Findings',
    sectionRel:              'Relationships',
    fRef:                    'Reference',
    fType:                   'Type',
    fAuditor:                'Auditor',
    fProgrammeYear:          'Audit program year',
    fIndependence:           'Independent auditor',
    fMethods:                'Audit methods',
    fPlannedDate:            'Planned date',
    fActualDate:             'Actual date',
    fScope:                  'Scope',
    fCriteria:               'Criteria',
    fStatus:                 'Status',
    fReportPath:             'Report path',
    fReportPathPh:           'e.g. audits/report-2026.pdf',
    fEvidenceIds:            'Evidence IDs',
    evidencePlaceholder:     'Enter evidence ID and press Enter…',
    fTags:                   'Tags',
    fFindingEvidenceIds:     'Evidence IDs',
    methodLabels:            {
      'Interview':       'Interview',
      'Observation':     'Observation',
      'Document review': 'Document review',
      'Sampling':        'Sampling',
      'Testing':         'Testing',
    },
    tagPlaceholder:          'Add and press Enter…',
    findingsHeader:          'findings',
    findingColId:            'ID',
    findingColType:          'Type',
    findingColClause:        'Clause',
    findingColControl:       'Control',
    findingColDesc:          'Description',
    findingColRisk:          'Risk impact',
    findingColPriority:      'Priority',
    findingColRec:           'Recommendation',
    findingColNcRef:         'NC Ref',
    findingColActions:       'Actions',
    findingType:             'Type',
    findingClause:           'Clause ref',
    findingControl:          'Annex A control',
    findingDesc:             'Description',
    findingRisk:             'Risk impact',
    findingPriority:         'Priority',
    findingRec:              'Recommendation',
    createNcBtn:             'Create NC',
    createNcTitle:           'Create nonconformity from finding',
    createNcOwnerLabel:      'Owner *',
    createNcConfirm:         'Create NC',
    createNcCancel:          'Cancel',
    createNcUnsaved:         'There are unsaved changes. Continue without saving and create the NC?',
    ncBadgePrefix:           'NC:',
    addFinding:              'Add finding',
    showFindingForm:         '+ Add finding',
    hideFindingForm:         '− Close',
    deleteFindingConfirm:    'Delete this finding?',
    findingsSummaryNone:     'No findings',
    findingValidationError:  'Complete the required finding fields before adding.',
    findingClausePh:         'Search clause…',
    findingControlPh:        'Search Annex A control…',
    clauseOrControlRequired: 'You must indicate at least one clause or Annex A control.',
    formSave:                'Save audit',
    formCancel:              'Cancel',
    validationError:         'Please complete the required fields marked with *.',
    aiError:                 'Could not get suggestion. Please try again.',
    aiErrorOverloaded:       'Servers are busy, please try again in a moment.',
    aiConsentRequired:       'Enable AI processing in Settings to use this feature.',
    colRef:                  'Reference',
    colType:                 'Type',
    colStatus:               'Status',
    colAuditor:              'Auditor',
    colPlannedDate:          'Planned date',
    colFindings:             'Findings',
    colActions:              'Actions',
    typeLabels: {
      Internal:        'Internal',
      Stage1:          'Stage 1',
      Stage2:          'Stage 2',
      Surveillance:    'Surveillance',
      Recertification: 'Recertification',
    },
    statusLabels: {
      'Planned':     'Planned',
      'In Progress': 'In Progress',
      'Completed':   'Completed',
    },
    findingLabels: {
      'Major NC':                    'Major NC',
      'Minor NC':                    'Minor NC',
      'Observation':                 'Observation',
      'Opportunity for Improvement': 'Opportunity for Improvement',
    },
    priorityLabels: {
      High:   'High',
      Medium: 'Medium',
      Low:    'Low',
    },
    filterType:         'Type',
    filterStatus:       'Status',
    filterFindings:     'Finding type',
    filterAll:          'All',
    filterNoFindings:   'No findings',
    filterNSelected:    '{n} selected',
    filterNoSelection:  'All',
    required:           '*',
    requiredLegend:     '* Required fields',
    frozenBanner:       '🔒 This record is read-only. To modify it, reopen from Settings → Reopen closed records.',
    badgeOverdue:       'Overdue',
    badgeSoon:          'Due soon',
    deleteBlockedTitle:        'Cannot delete: references found',
    deleteBlockedReferencedBy: 'Referenced by',
    deleteBlockedOpenHolder:   'Open',
    deactivateInstead:         'Deactivate instead',
    bulkDeleteSummary_allBlocked: 'None of the selected records can be deleted because all of them are referenced by other records.',
    bulkDeleteSummary_mixed:      '{deletable} of {total} records can be deleted; {blocked} are blocked by references.',
    continueWithDeletables:       'Delete the unblocked records',
    deleteBlockedHolder_risk_one:               'risk',
    deleteBlockedHolder_risk_other:             'risks',
    deleteBlockedHolder_asset_one:              'asset',
    deleteBlockedHolder_asset_other:            'assets',
    deleteBlockedHolder_supplier_one:           'supplier',
    deleteBlockedHolder_supplier_other:         'suppliers',
    deleteBlockedHolder_incident_one:           'incident',
    deleteBlockedHolder_incident_other:         'incidents',
    deleteBlockedHolder_audit_one:              'audit',
    deleteBlockedHolder_audit_other:            'audits',
    deleteBlockedHolder_nonconformity_one:      'nonconformity',
    deleteBlockedHolder_nonconformity_other:    'nonconformities',
    deleteBlockedHolder_objective_one:          'objective',
    deleteBlockedHolder_objective_other:        'objectives',
    deleteBlockedHolder_metric_one:             'metric',
    deleteBlockedHolder_metric_other:           'metrics',
    deleteBlockedHolder_document_one:           'document',
    deleteBlockedHolder_document_other:         'documents',
    'deleteBlockedHolder_legal-register_one':   'legal register entry',
    'deleteBlockedHolder_legal-register_other': 'legal register entries',
    'deleteBlockedHolder_management-review_one':   'management review',
    'deleteBlockedHolder_management-review_other': 'management reviews',
    'deleteBlockedHolder_soa-control_one':      'SoA control',
    'deleteBlockedHolder_soa-control_other':    'SoA controls',
    deleteBlockedHolder_scope_one:              'scope',
    deleteBlockedHolder_scope_other:            'scope',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function makeAudId(dateStr, seq) {
  const compact = (dateStr || todayStr()).replace(/-/g, '');
  return `aud-${compact}-${String(seq).padStart(3, '0')}`;
}

function makeRef(type, seq, auditPrefix) {
  const year = new Date().getFullYear();
  const abbrev = TYPE_ABBREV[type] || 'INT';
  return `${auditPrefix}-${year}-${abbrev}-${String(seq).padStart(3, '0')}`;
}

function blankNewFinding() {
  return {
    type: '', clauseRefs: [], linkedControlIds: [],
    description: '', riskImpact: '', priority: '',
    recommendation: '',
    evidenceIds: [],
  };
}

function blankFormValues(seq, auditPrefix) {
  const today = todayStr();
  return {
    id:                  makeAudId(today, seq),
    ref:                 makeRef('Internal', seq, auditPrefix),
    type:                'Internal',
    auditProgrammeYear:  new Date().getFullYear(),
    independence:        true,
    auditMethods:        [],
    auditor:             '',
    plannedDate:         '',
    actualDate:          '',
    scope:               '',
    criteria:            [],
    status:              'Planned',
    reportPath:          '',
    findings:            [],
    evidenceIds:         [],
    tags:                [],
    createdAt:           new Date().toISOString(),
  };
}

function auditToFormValues(audit) {
  return {
    id:                  audit.id,
    ref:                 audit.ref         || '',
    type:                audit.type        || 'Internal',
    auditProgrammeYear:  audit.auditProgrammeYear || new Date().getFullYear(),
    independence:        typeof audit.independence === 'boolean' ? audit.independence : true,
    auditMethods:        Array.isArray(audit.auditMethods) ? audit.auditMethods : [],
    auditor:             audit.auditor     || '',
    plannedDate:         audit.plannedDate || '',
    actualDate:          audit.actualDate  || '',
    scope:               audit.scope       || '',
    criteria:            Array.isArray(audit.criteria) ? audit.criteria : [],
    status:              audit.status      || 'Planned',
    reportPath:          audit.reportPath  || '',
    findings:            Array.isArray(audit.findings) ? audit.findings : [],
    evidenceIds:         Array.isArray(audit.evidenceIds) ? audit.evidenceIds : [],
    tags:                Array.isArray(audit.tags) ? audit.tags : [],
    createdAt:           audit.createdAt   || new Date().toISOString(),
  };
}

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE,
      border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 10,
      padding: '0.9rem 1.25rem',
      flex: 1,
      minWidth: 90,
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        ...FONT_STAT_VALUE,
        color: color || TEXT_HEADING,
      }}>
        {value}
      </div>
    </div>
  );
}

function InlineBadge({ label, colors, title: tip }) {
  return (
    <span
      title={tip}
      style={{
        ...FONT_SMALL,
        background: colors.bg, color: colors.text,
        borderRadius: 4, padding: '0.15rem 0.45rem',
        fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ── LIST TABLE STYLES (homologated with RiskList) ────────────────────────────
const LIST_TH_STYLE = {
  ...FONT_TINY,
  color: TEXT_MUTED,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '0.6rem 0.75rem',
  textAlign: 'left',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
  whiteSpace: 'nowrap',
  background: BG_SUBTLE,
};

const LIST_TD_STYLE = {
  ...FONT_BODY,
  color: TEXT_DEFAULT,
  padding: '0.7rem 0.75rem',
  borderBottom: `1px solid ${BG_SHELL}`,
  verticalAlign: 'middle',
};

// ── AUDIT ROW ─────────────────────────────────────────────────────────────────
function AuditRow({ audit, lang, onEdit, onView, onDelete, checked, onToggle, people }) {
  const t = T[lang] || T.en;
  const statusColors = STATUS_COLORS[audit.status] || STATUS_COLORS['Planned'];
  const findings = Array.isArray(audit.findings) ? audit.findings : [];

  const majorNC = findings.filter(f => f.type === 'Major NC').length;
  const minorNC = findings.filter(f => f.type === 'Minor NC').length;
  const obs     = findings.filter(f => f.type === 'Observation').length;
  const ofi     = findings.filter(f => f.type === 'Opportunity for Improvement').length;

  const findingChips = [
    majorNC > 0 && { label: `${majorNC} ${t.findingLabels['Major NC']}`, tip: `${majorNC} ${t.findingLabels['Major NC']}`, colors: FINDING_TYPE_COLORS['Major NC'] },
    minorNC > 0 && { label: `${minorNC} ${t.findingLabels['Minor NC']}`, tip: `${minorNC} ${t.findingLabels['Minor NC']}`, colors: FINDING_TYPE_COLORS['Minor NC'] },
    obs > 0     && { label: `${obs} ${t.findingLabels['Observation']}`, tip: `${obs} ${t.findingLabels['Observation']}`, colors: FINDING_TYPE_COLORS['Observation'] },
    ofi > 0     && { label: `${ofi} ${lang === 'es' ? 'OM' : 'OFI'}`, tip: t.findingLabels['Opportunity for Improvement'], colors: FINDING_TYPE_COLORS['Opportunity for Improvement'] },
  ].filter(Boolean);

  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_SMALL,
    borderRadius: 6,
    padding: '0.25rem 0.7rem', cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  return (
    <tr>
      <td style={{ ...LIST_TD_STYLE, textAlign: 'center', padding: '0.55rem 0.5rem' }}>
        <input type="checkbox" checked={checked} onChange={(e) => onToggle(audit.id, e.target.checked)} />
      </td>
      <td style={{ ...LIST_TD_STYLE, ...FONT_LABEL, fontWeight: 500, color: TEXT_HEADING, whiteSpace: 'nowrap' }}>
        {audit.ref}
      </td>
      <td style={LIST_TD_STYLE}>
        <InlineBadge
          label={t.typeLabels[audit.type] || audit.type}
          colors={{ bg: '#e0e7ef', text: TEXT_DEFAULT }}
        />
      </td>
      <td style={LIST_TD_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <InlineBadge label={t.statusLabels[audit.status] || audit.status} colors={statusColors} />
          {(() => {
            if (audit.status === 'Completed') return null;
            const ds = dateStatus(audit.plannedDate);
            if (!ds) return null;
            return (
              <span style={{
                ...FONT_TINY,
                fontWeight: 600,
                background: ds === 'overdue' ? STATUS_BAD.bg : STATUS_WARN.bg,
                color:      ds === 'overdue' ? STATUS_BAD.text : STATUS_WARN.text,
                borderRadius: 4,
                padding: '0.15rem 0.45rem',
                marginLeft: '0.35rem',
                whiteSpace: 'nowrap',
              }}>
                {ds === 'overdue' ? t.badgeOverdue : t.badgeSoon}
              </span>
            );
          })()}
        </div>
      </td>
      <td style={{ ...LIST_TD_STYLE, whiteSpace: 'nowrap' }}>
        {resolvePerson(audit.auditor, people) || '—'}
      </td>
      <td style={{ ...LIST_TD_STYLE, ...FONT_LABEL, whiteSpace: 'nowrap' }}>
        {audit.plannedDate || '—'}
      </td>
      <td style={LIST_TD_STYLE}>
        {findingChips.length === 0 ? (
          <span style={{ ...FONT_SMALL, color: TEXT_DISABLED }}>
            {t.findingsSummaryNone}
          </span>
        ) : (
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            {findingChips.map((chip, i) => (
              <InlineBadge key={i} label={chip.label} colors={chip.colors} title={chip.tip} />
            ))}
          </div>
        )}
      </td>
      <td style={{ ...LIST_TD_STYLE, whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <button onClick={() => onView(audit)} style={btnSmall}>{t.viewBtn}</button>
          <button onClick={() => onEdit(audit)} style={btnSmall}>{t.editBtn}</button>
          <button onClick={() => onDelete(audit)} style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}>{t.deleteBtn}</button>
        </div>
      </td>
    </tr>
  );
}

// ── AUDIT FORM ────────────────────────────────────────────────────────────────
function AuditForm({ t, lang, mode, auditId, initialValues, onSave, onCancel, onDelete, org, saving, saveError, onFormChange, saveRequestTick, dirty, onCreateNcFromFinding }) {
  const isView = mode === 'view';
  // §11 rule 34 — read-only banner shown only when the form is in view mode
  // because the audit is in a terminal/immutable status (Completed), not when
  // the user voluntarily opened a non-immutable audit in view mode.
  const frozen = isView && isRecordImmutable({ status: initialValues.status }, 'audit');
  const [values, setValues]         = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [newFinding, setNewFinding] = useState(blankNewFinding());
  const [findingErr, setFindingErr] = useState(false);
  const [showFindingPanel, setShowFindingPanel] = useState(false);
  const [aiSuggestingAudit, setAiSuggestingAudit] = useState(false);
  const [aiErrorAudit, setAiErrorAudit] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const hasChangedRef = useRef(false);

  const set = (field, val) => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
    setValues(v => ({ ...v, [field]: val }));
  };

  const inputSt = (hasErr) => ({
    ...FONT_LABEL,
    fontSize: '0.85rem',
    width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box',
    border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
    borderRadius: 6,
    background: SURFACE_WHITE, color: TEXT_HEADING, outline: 'none',
  });

  const labelSt = {
    ...FONT_SMALL,
    color: TEXT_MUTED, letterSpacing: '0.03em',
    textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
  };

  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: '0.15rem' };

  const sectionBar = {
    ...FONT_BODY_STRONG,
    color: TEXT_HEADING,
    margin: '0 0 1rem',
    paddingBottom: '0.5rem',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
  };

  // Auto-update ref when type changes if it still matches the auto-generated pattern
  const handleTypeChange = (newType) => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
    const seqMatch = values.ref.match(/(\d{3})$/);
    const seq = seqMatch ? parseInt(seqMatch[1], 10) : 1;
    const year = new Date().getFullYear();
    const auditPrefix = (org.refPrefixes && org.refPrefixes.audit) || 'aud';
    const autoPattern = new RegExp(`^${auditPrefix}-${year}-[A-Z0-9]{3}-\\d{3}$`);
    const newRef = autoPattern.test(values.ref)
      ? `${auditPrefix}-${year}-${TYPE_ABBREV[newType] || 'INT'}-${String(seq).padStart(3, '0')}`
      : values.ref;
    setValues(v => ({ ...v, type: newType, ref: newRef }));
  };

  const handleAISuggestAudit = async () => {
    setAiSuggestingAudit(true);
    setAiErrorAudit(null);
    const prompt = `You are an ISO 27001:2022 internal audit expert.\nGiven the following audit details, suggest an appropriate scope and criteria.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nAudit type: ${values.type}\nPlanned date: ${values.plannedDate}\nISMS scope: ${org.scope || ''}\nReturn exactly this JSON:\n{\n"scope": "specific audit scope statement in 1-2 sentences",\n"criteria": ["..."]\n}\ncriteria 3-5 ISO 27001:2022 clauses or Annex A controls relevant to this audit type.`;
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorAudit(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingAudit(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorAudit(t.aiError);
      setAiSuggestingAudit(false);
      return;
    }
    if (parsed.scope && !values.scope.trim()) set('scope', parsed.scope);
    if (Array.isArray(parsed.criteria) && (!Array.isArray(values.criteria) || values.criteria.length === 0)) set('criteria', parsed.criteria);
    setAiSuggestingAudit(false);
  };

  // New finding field setter
  const setNF = (field, val) => setNewFinding(nf => ({ ...nf, [field]: val }));

  const nfInputSt = (errField) => ({
    ...FONT_LABEL,
    width: '100%', padding: '0.35rem 0.5rem', boxSizing: 'border-box',
    border: `1px solid ${(errField && findingErr) ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
    borderRadius: 5,
    background: SURFACE_WHITE, color: TEXT_HEADING, outline: 'none', resize: 'vertical',
  });

  // Options for the finding clause/control autocompletes.
  const clauseOptions = useMemo(() => (
    Object.entries(ISO_CLAUSES).map(([id, titles]) => ({
      id,
      label: `${id} — ${titles[lang] || titles.en}`,
    }))
  ), [lang]);

  const controlOptions = useMemo(() => (
    SOA_CONTROLS.map((c) => ({
      id: c.id,
      label: `A.${c.id} — ${lang === 'es' ? c.title_es : c.title}`,
    }))
  ), [lang]);

  const handleAddFinding = () => {
    const required = ['type', 'description', 'riskImpact', 'priority', 'recommendation'];
    const missingCore = required.some(f => !newFinding[f].trim());
    const missingClauseAndControl =
      newFinding.clauseRefs.length === 0 && newFinding.linkedControlIds.length === 0;
    if (missingCore || missingClauseAndControl) { setFindingErr(true); return; }
    setFindingErr(false);
    const seq = String(values.findings.length + 1).padStart(3, '0');
    const finding = {
      id:               `finding-${seq}`,
      type:             newFinding.type,
      clauseRefs:       newFinding.clauseRefs,
      linkedControlIds: newFinding.linkedControlIds,
      description:      newFinding.description.trim(),
      riskImpact:       newFinding.riskImpact.trim(),
      priority:         newFinding.priority,
      recommendation:   newFinding.recommendation.trim(),
      nonconformityId: null,
      evidenceIds:      Array.isArray(newFinding.evidenceIds) ? newFinding.evidenceIds : [],
    };
    set('findings', [...values.findings, finding]);
    setNewFinding(blankNewFinding());
    setShowFindingPanel(false);
  };

  const handleDeleteFinding = (idx) => {
    setConfirmState({
      message: t.deleteFindingConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        const updated = values.findings
          .filter((_, i) => i !== idx)
          .map((f, i) => ({ ...f, id: `finding-${String(i + 1).padStart(3, '0')}` }));
        set('findings', updated);
      },
    });
  };

  const handleSubmit = () => {
    const errs = {};
    if (!values.ref.trim())      errs.ref         = true;
    if (!values.type)            errs.type        = true;
    if (!values.auditor.trim())  errs.auditor     = true;
    if (!values.plannedDate)     errs.plannedDate = true;
    if (!values.scope.trim())    errs.scope       = true;
    if (!values.status)          errs.status      = true;
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onSave(values);
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const hasErrors = Object.keys(formErrors).length > 0;

  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 12, padding: '2rem', marginTop: '1.5rem',
      position: 'relative',
    }}>
      <button
        type="button"
        onClick={onCancel}
        style={{
          ...FONT_LABEL,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: BRAND_PRIMARY, marginBottom: '0.5rem', display: 'block',
        }}
      >
        {t.backToList}
      </button>
      <h3 style={{
        ...FONT_SECTION_TITLE,
        color: TEXT_HEADING, margin: '0 0 1.5rem',
      }}>
        {isView ? t.formTitleView : (auditId === 'new' ? t.formTitleNew : t.formTitleEdit)}
      </h3>
      {frozen && (
        <div style={{
          background: STATUS_NEUTRAL.bg, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
          ...FONT_LABEL, color: TEXT_DEFAULT,
        }}>
          {t.frozenBanner}
        </div>
      )}
      {!isView && (
        <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 2rem', letterSpacing: '0.04em' }}>{t.requiredLegend}</p>
      )}
      <fieldset disabled={isView || saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── SECTION A ─────────────────────────────────────────────────────── */}
      <div style={sectionBar}>A — {t.sectionA}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        {/* Ref */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fRef} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <input
            type="text" value={values.ref}
            onChange={e => set('ref', e.target.value)}
            style={inputSt(formErrors.ref)}
          />
        </div>
        {/* Type */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fType} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <select
            value={values.type}
            onChange={e => handleTypeChange(e.target.value)}
            style={inputSt(formErrors.type)}
          >
            {AUDIT_TYPES.map(tp => (
              <option key={tp} value={tp}>{t.typeLabels[tp]}</option>
            ))}
          </select>
        </div>
        {/* Auditor */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fAuditor} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <PeopleSelector
            orgId={org.id}
            lang={lang}
            value={values.auditor}
            onChange={(v) => set('auditor', v)}
          />
        </div>
        {/* Status */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fStatus} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <select
            value={values.status}
            onChange={e => set('status', e.target.value)}
            style={inputSt(formErrors.status)}
          >
            {AUDIT_STATUSES.map(s => <option key={s} value={s}>{t.statusLabels[s]}</option>)}
          </select>
        </div>
        {/* Planned date */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fPlannedDate} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <input
            type="date" value={values.plannedDate}
            onChange={e => set('plannedDate', e.target.value)}
            style={inputSt(formErrors.plannedDate)}
          />
        </div>
        {/* Actual date */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fActualDate}</label>
          <input
            type="date" value={values.actualDate}
            onChange={e => set('actualDate', e.target.value)}
            style={inputSt(false)}
          />
        </div>
        {/* Audit programme year */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fProgrammeYear}</label>
          <input
            type="number"
            value={values.auditProgrammeYear}
            onChange={e => set('auditProgrammeYear', parseInt(e.target.value, 10) || new Date().getFullYear())}
            style={inputSt(false)}
          />
        </div>
        {/* Independence */}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fIndependence}</label>
          <label style={{ ...FONT_LABEL, display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isView ? 'default' : 'pointer',
            color: TEXT_DEFAULT, padding: '0.4rem 0' }}>
            <input
              type="checkbox"
              checked={!!values.independence}
              onChange={e => set('independence', e.target.checked)}
              disabled={isView}
            />
            {t.fIndependence}
          </label>
        </div>
      </div>

      {/* Audit methods */}
      <div style={{ ...fieldWrap, marginBottom: '1rem' }}>
        <label style={labelSt}>{t.fMethods}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {AUDIT_METHODS.map((m) => {
            const active = (values.auditMethods || []).includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => {
                  if (isView) return;
                  const cur = Array.isArray(values.auditMethods) ? values.auditMethods : [];
                  set('auditMethods', active ? cur.filter(x => x !== m) : [...cur, m]);
                }}
                disabled={isView}
                style={{
                  ...FONT_SMALL,
                  background: active ? TEXT_HEADING : SURFACE_WHITE,
                  color: active ? SURFACE_WHITE : TEXT_DEFAULT,
                  border: `1px solid ${active ? TEXT_HEADING : BORDER_DEFAULT}`,
                  borderRadius: 16, padding: '0.25rem 0.7rem',
                  cursor: isView ? 'default' : 'pointer',
                }}
              >
                {t.methodLabels[m] || m}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scope */}
      <div style={{ ...fieldWrap, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', marginBottom: '0.3rem' }}>
          <label style={{ ...labelSt, marginBottom: 0 }}>{t.fScope} <span style={{ color: ERROR_TEXT }}>{t.required}</span><span className="ai-spark">✦</span></label>
          <AISuggestButton onClick={handleAISuggestAudit} loading={aiSuggestingAudit} disabled={!values.type || !org.aiProcessingConsent} lang={lang} />
          {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
        </div>
        {aiErrorAudit && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.3rem' }}>{aiErrorAudit}</p>}
        <textarea
          rows={2} value={values.scope}
          onChange={e => set('scope', e.target.value)}
          style={{ ...inputSt(formErrors.scope), resize: 'vertical' }}
        />
      </div>

      {/* Criteria */}
      <div style={{ ...fieldWrap, marginBottom: '1rem' }}>
        <label style={labelSt}>{t.fCriteria}<span className="ai-spark">✦</span></label>
        <TagEditor
          tags={values.criteria}
          onChange={val => set('criteria', val)}
          placeholder={t.tagPlaceholder}
        />
      </div>

      {/* Report path */}
      <div style={{ ...fieldWrap, marginBottom: '1.75rem' }}>
        <label style={labelSt}>{t.fReportPath}</label>
        <input
          type="text" value={values.reportPath}
          onChange={e => set('reportPath', e.target.value)}
          placeholder={t.fReportPathPh}
          style={inputSt(false)}
        />
      </div>

      {/* ── SECTION A2 — RELATIONSHIPS ───────────────────────────────────── */}
      <div style={sectionBar}>{t.sectionRel}</div>
      <div style={{ ...fieldWrap, marginBottom: '1rem' }}>
        <label style={labelSt}>{t.fEvidenceIds}</label>
        <TagInput
          tags={values.evidenceIds}
          onAdd={(val) => set('evidenceIds', [...values.evidenceIds, val])}
          onRemove={(val) => set('evidenceIds', values.evidenceIds.filter(x => x !== val))}
          onUpdate={(oldValue, newValue) => set('evidenceIds', values.evidenceIds.map((x) => x === oldValue ? newValue : x))}
          placeholder={t.evidencePlaceholder}
          disabled={isView}
        />
      </div>
      <div style={{ ...fieldWrap, marginBottom: '1.75rem' }}>
        <label style={labelSt}>{t.fTags}</label>
        <TagEditor
          tags={values.tags}
          onChange={(val) => set('tags', val)}
          placeholder={t.tagPlaceholder}
        />
      </div>

      {/* ── SECTION B — FINDINGS ──────────────────────────────────────────── */}
      <div style={sectionBar}>
        B — {t.sectionB}
        <span style={{
          background: '#e0e7ef', color: TEXT_DEFAULT,
          borderRadius: 10, padding: '0.1rem 0.5rem',
          fontSize: '0.65rem', fontWeight: 700,
        }}>
          {values.findings.length}
        </span>
      </div>

      {/* Existing findings table */}
      {values.findings.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
          <table style={{
            ...FONT_LABEL,
            width: '100%', borderCollapse: 'collapse',
          }}>
            <thead>
              <tr style={{ background: BG_MUTED }}>
                {[
                  t.findingColId, t.findingColType, t.findingColClause,
                  t.findingColControl, t.findingColDesc, t.findingColRisk,
                  t.findingColPriority, t.findingColRec,
                  t.findingColNcRef, t.findingColActions,
                ].map(col => (
                  <th key={col} style={{
                    padding: '0.4rem 0.5rem', textAlign: 'left',
                    fontWeight: 600, color: TEXT_MUTED, fontSize: '0.68rem',
                    letterSpacing: '0.03em', borderBottom: `1px solid ${BORDER_DEFAULT}`,
                    whiteSpace: 'nowrap',
                  }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {values.findings.map((f, idx) => {
                const ftC = FINDING_TYPE_COLORS[f.type] || { bg: BG_MUTED, text: TEXT_MUTED };
                const prC = PRIORITY_COLORS[f.priority] || { bg: BG_MUTED, text: TEXT_MUTED };
                return (
                  <tr key={f.id} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                    <td style={{ padding: '0.4rem 0.5rem', color: TEXT_MUTED, whiteSpace: 'nowrap' }}>{f.id}</td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                      <span title={f.type} style={{
                        background: ftC.bg, color: ftC.text,
                        borderRadius: 4, padding: '0.1rem 0.35rem',
                        fontSize: '0.68rem', fontWeight: 600,
                      }}>
                        {t.findingLabels[f.type] || f.type}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>{(f.clauseRefs || []).join(', ') || '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>{(f.linkedControlIds || []).length > 0 ? (f.linkedControlIds || []).map(id => `A.${id}`).join(', ') : '—'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', maxWidth: 160 }}>{f.description}</td>
                    <td style={{ padding: '0.4rem 0.5rem', maxWidth: 140 }}>{f.riskImpact}</td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                      <span style={{
                        background: prC.bg, color: prC.text,
                        borderRadius: 4, padding: '0.1rem 0.35rem',
                        fontSize: '0.68rem', fontWeight: 600,
                      }}>
                        {t.priorityLabels[f.priority] || f.priority}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', maxWidth: 160 }}>{f.recommendation}</td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                      {f.nonconformityId ? (
                        <span style={{
                          ...FONT_SMALL,
                          background: STATUS_NEUTRAL.bg,
                          color: STATUS_NEUTRAL.text,
                          borderRadius: 4,
                          padding: '0.1rem 0.35rem',
                          fontWeight: 600,
                        }}>
                          {t.ncBadgePrefix} {f.nonconformityId}
                        </span>
                      ) : (
                        !isView && (f.type === 'Major NC' || f.type === 'Minor NC') ? (
                          <button
                            type="button"
                            onClick={() => onCreateNcFromFinding(idx)}
                            style={{
                              ...BTN_SECONDARY,
                              ...FONT_SMALL,
                              borderRadius: 5,
                              padding: '0.15rem 0.4rem',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {t.createNcBtn}
                          </button>
                        ) : '—'
                      )}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => handleDeleteFinding(idx)}
                        style={{
                          ...FONT_SMALL,
                          background: 'none', border: `1px solid ${ERROR_BORDER}`,
                          color: ERROR_TEXT, borderRadius: 4,
                          padding: '0.15rem 0.4rem', cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Toggle button for the finding entry panel */}
      <div style={{ marginBottom: showFindingPanel ? '0.6rem' : '1.75rem' }}>
        <button
          type="button"
          onClick={() => {
            if (showFindingPanel) {
              setFindingErr(false);
              setNewFinding(blankNewFinding());
            }
            setShowFindingPanel(!showFindingPanel);
          }}
          style={{
            ...FONT_LABEL,
            background: 'none', border: `1px dashed ${BORDER_DEFAULT}`, borderRadius: 8,
            padding: '0.45rem 1rem', cursor: 'pointer',
            color: TEXT_DEFAULT, letterSpacing: '0.02em',
          }}
        >
          {showFindingPanel ? t.hideFindingForm : t.showFindingForm}
        </button>
      </div>

      {/* Add finding row (collapsible) */}
      {showFindingPanel && (
      <div style={{
        background: BG_MUTED, border: `1px solid ${STATUS_NEUTRAL.bg}`,
        borderRadius: 8, padding: '1rem', marginBottom: '1.75rem',
      }}>
        {/* Row 1: type / clause / control */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>
              {t.findingType} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            </label>
            <select
              value={newFinding.type}
              onChange={e => setNF('type', e.target.value)}
              style={nfInputSt(!newFinding.type)}
            >
              <option value="">—</option>
              {FINDING_TYPES.map(ft => <option key={ft} value={ft}>{t.findingLabels[ft]}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>
              {t.findingClause} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            </label>
            <EntitySelector
              items={clauseOptions}
              selectedIds={newFinding.clauseRefs}
              onAdd={(id) => setNF('clauseRefs', [...newFinding.clauseRefs, id])}
              onRemove={(id) => setNF('clauseRefs', newFinding.clauseRefs.filter(x => x !== id))}
              placeholder={t.findingClausePh}
            />
          </div>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.findingControl}</label>
            <EntitySelector
              items={controlOptions}
              selectedIds={newFinding.linkedControlIds}
              onAdd={(id) => setNF('linkedControlIds', [...newFinding.linkedControlIds, id])}
              onRemove={(id) => setNF('linkedControlIds', newFinding.linkedControlIds.filter(x => x !== id))}
              placeholder={t.findingControlPh}
            />
          </div>
        </div>

        {/* Row 2: description / priority / risk impact */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>
              {t.findingDesc} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            </label>
            <textarea
              rows={2} value={newFinding.description}
              onChange={e => setNF('description', e.target.value)}
              style={nfInputSt(!newFinding.description)}
            />
          </div>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>
              {t.findingPriority} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            </label>
            <select
              value={newFinding.priority}
              onChange={e => setNF('priority', e.target.value)}
              style={nfInputSt(!newFinding.priority)}
            >
              <option value="">—</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{t.priorityLabels[p] || p}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>
              {t.findingRisk} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            </label>
            <textarea
              rows={2} value={newFinding.riskImpact}
              onChange={e => setNF('riskImpact', e.target.value)}
              style={nfInputSt(!newFinding.riskImpact)}
            />
          </div>
        </div>

        {/* Row 3: recommendation */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>
              {t.findingRec} <span style={{ color: ERROR_TEXT }}>{t.required}</span>
            </label>
            <textarea
              rows={2} value={newFinding.recommendation}
              onChange={e => setNF('recommendation', e.target.value)}
              style={nfInputSt(!newFinding.recommendation)}
            />
          </div>
        </div>

        {/* Row 4: finding evidence IDs */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={fieldWrap}>
            <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fFindingEvidenceIds}</label>
            <TagInput
              tags={newFinding.evidenceIds}
              onAdd={(val) => setNF('evidenceIds', [...newFinding.evidenceIds, val])}
              onRemove={(val) => setNF('evidenceIds', newFinding.evidenceIds.filter(x => x !== val))}
              onUpdate={(oldValue, newValue) => setNF('evidenceIds', newFinding.evidenceIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.evidencePlaceholder}
            />
          </div>
        </div>

        {findingErr && (
          <div style={{
            ...FONT_LABEL,
            color: ERROR_TEXT, marginBottom: '0.5rem',
          }}>
            {newFinding.clauseRefs.length === 0 && newFinding.linkedControlIds.length === 0
              ? t.clauseOrControlRequired
              : t.findingValidationError}
          </div>
        )}

        <button
          type="button"
          onClick={handleAddFinding}
          style={{
            ...FONT_LABEL,
            background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
            borderRadius: 6, padding: '0.4rem 1rem', cursor: 'pointer',
            letterSpacing: '0.02em',
          }}
        >
          {t.addFinding}
        </button>
      </div>
      )}

      </fieldset>

      {/* Sticky action bar */}
      {!isView && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginLeft: '-2rem',
          marginRight: '-2rem',
          marginBottom: '-2rem',
          marginTop: '1.5rem',
          padding: '0.85rem 2rem',
          background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 10,
        }}>
          {hasErrors && (
            <div style={{
              ...FONT_LABEL,
              color: ERROR_TEXT, marginBottom: '0.5rem',
            }}>
              {t.validationError}
            </div>
          )}
          {saveError && (
            <div style={{
              ...FONT_LABEL,
              background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
              borderRadius: 6, padding: '0.35rem 0.75rem',
              marginBottom: '0.5rem',
            }}>{t.saveError}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div>
              {auditId !== 'new' && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={saving}
                  style={{
                    ...BTN_DESTRUCTIVE_OUTLINE,
                    ...FONT_LABEL,
                    borderRadius: 8,
                    padding: '0.5rem 1rem',
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >{t.deleteFormBtn}</button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {dirty && (
                <span style={{
                  ...FONT_LABEL,
                  color: UNSAVED_AMBER,
                }}>{t.unsavedLabel}</span>
              )}
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                style={{
                  ...BTN_SECONDARY,
                  ...FONT_BUTTON,
                  fontWeight: 400,
                  borderRadius: 8,
                  padding: '0.6rem 1.25rem', cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {t.formCancel}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                style={{
                  ...BTN_PRIMARY,
                  ...FONT_BUTTON,
                  fontWeight: 700,
                  background: saving ? TEXT_MUTED : BTN_PRIMARY.background,
                  border: 'none',
                  borderRadius: 8, padding: '0.6rem 1.6rem', cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? t.saving : t.formSave}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
      />
    </div>
  );
}

// ── AUDITS VIEW ───────────────────────────────────────────────────────────────
// ── MULTI-SELECT FILTER DROPDOWN ──────────────────────────────────────────────
function MultiSelectFilter({ options, values, onChange, allLabel, nSelectedTpl, getLabel }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter(x => x !== v));
    else                    onChange([...values, v]);
  };

  const summary = values.length === 0
    ? allLabel
    : nSelectedTpl.replace('{n}', values.length);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...FONT_LABEL,
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6,
          padding: '0.3rem 0.5rem',
          color: TEXT_DEFAULT,
          cursor: 'pointer', textAlign: 'left', minWidth: 160,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ color: TEXT_DISABLED, fontSize: '0.7rem' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 2, zIndex: 200,
          background: SURFACE_WHITE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          minWidth: 220, maxHeight: 260, overflowY: 'auto', padding: '0.3rem 0',
        }}>
          {options.length === 0 && (
            <div style={{ ...FONT_LABEL, padding: '0.4rem 0.65rem', color: TEXT_DISABLED }}>—</div>
          )}
          {options.map(opt => {
            const checked = values.includes(opt);
            return (
              <label
                key={opt}
                style={{ ...FONT_LABEL, display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.65rem', color: TEXT_DEFAULT, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = BG_CONTENT; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(opt)} />
                <span>{getLabel ? getLabel(opt) : opt}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
export default function AuditsView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick: appSaveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [allAudits, setAllAudits] = useState(null);   // null = loading
  const [people, setPeople] = useState([]);
  const [editingId, setEditingId] = useState(null);   // null | 'new' | audit.id
  const [formMode, setFormMode] = useState('edit');   // 'create' | 'edit' | 'view'
  const [formInitial, setFormInitial] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saveState, setSaveState] = useState('idle');
  const [saving, setSaving]       = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);
  const [dirty, setDirty]               = useState(false);
  const [saveRequestTick, setSaveRequestTick] = useState(0);
  // ── Create-NC-from-finding mini-dialog state (§11 rule 34 narrow exception)
  const [ncDialog, setNcDialog]               = useState(null);
  const [ncDialogOwner, setNcDialogOwner]     = useState('');
  const [ncDialogSaving, setNcDialogSaving]   = useState(false);
  // App-level "Save and leave" — flagged when the App tick increases so
  // the next form save reports back via onSaveResult instead of running
  // the form-Cancel close-form path. See GRC_UI_PATTERNS.md §5.
  const pendingAppSaveRef               = useRef(false);
  const lastProcessedAppTickRef         = useRef(0);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterType,     setFilterType]     = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterFindings, setFilterFindings] = useState([]); // multi-select

  useScrollToTopOnFormOpen(editingId !== null);

  // ── Load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    listOrgCollection(org.id, 'audits')
      .then(items => setAllAudits(items))
      .catch(() => setAllAudits([]));
  }, [org.id]);

  useEffect(() => {
    readOrgFile(org.id, 'people.json')
      .then(data => setPeople(Array.isArray(data.people) ? data.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  // ── App-level guard: bridge App tick into local tick to fire the embedded
  //    form's existing saveRequestTick handler. See GRC_UI_PATTERNS.md §5.
  useEffect(() => {
    if (typeof appSaveRequestTick !== 'number') return;
    if (appSaveRequestTick <= lastProcessedAppTickRef.current) return;
    lastProcessedAppTickRef.current = appSaveRequestTick;
    if (editingId !== null && formInitial !== null) {
      pendingAppSaveRef.current = true;
      setSaveRequestTick(n => n + 1);
    } else {
      onSaveResult?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSaveRequestTick]);

  // ── beforeunload browser guard ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── New ──────────────────────────────────────────────────────────────────
  const handleNew = async () => {
    const auditPrefix = (org.refPrefixes && org.refPrefixes.audit) || 'aud';
    let seq;
    try {
      const items = await listOrgCollection(org.id, 'audits');
      const year = new Date().getFullYear();
      let max = 0;
      items.forEach(a => {
        if (a.ref && a.ref.startsWith(`${auditPrefix}-${year}-`)) {
          const parts = a.ref.split('-');
          const n = parseInt(parts[parts.length - 1], 10);
          if (!isNaN(n) && n > max) max = n;
        }
      });
      seq = max + 1;
    } catch {
      const ts = Date.now().toString().slice(-4);
      seq = parseInt(ts, 10);
    }
    setFormInitial(blankFormValues(seq, auditPrefix));
    setFormMode('create');
    setEditingId('new');
    setDirty(false);
  };

  // ── Edit ─────────────────────────────────────────────────────────────────
  const handleEdit = (audit) => {
    setFormInitial(auditToFormValues(audit));
    // §11 rule 34 — Completed audits are immutable; open read-only even if
    // the user clicked Edit. Reopen is only available via Settings.
    setFormMode(isRecordImmutable(audit, 'audit') ? 'view' : 'edit');
    setEditingId(audit.id);
    setDirty(false);
  };

  // ── View ─────────────────────────────────────────────────────────────────
  const handleView = (audit) => {
    setFormInitial(auditToFormValues(audit));
    setFormMode('view');
    setEditingId(audit.id);
  };

  // ── Cancel ───────────────────────────────────────────────────────────────
  const closeForm = () => {
    setDirty(false);
    setEditingId(null);
    setFormInitial(null);
  };

  const handleCancel = () => {
    if (dirty) {
      setConfirmState({
        message: t.unsavedConfirm,
        confirmLabel: t.leaveWithoutSaving,
        onConfirm: () => { setConfirmState(null); closeForm(); },
        secondaryAction: {
          label: t.saveAndLeave,
          onClick: () => {
            setConfirmState(null);
            setSaveRequestTick(n => n + 1);
          },
        },
      });
      return;
    }
    closeForm();
  };

  // ── Form save → write directly to disk ───────────────────────────────────
  const handleFormSave = async (values) => {
    // §11 rule 34 — immutability after closure. Short-circuit before any
    // FileSystemAdapter write so a Completed audit can only be changed via
    // the Settings reopen flow.
    const existingAudit = (allAudits || []).find(a => a.id === values.id);
    if (existingAudit && isRecordImmutable(existingAudit, 'audit')) {
      setSaving(false);
      return;
    }
    setSaving(true);
    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';
    const audit = {
      id:                 values.id,
      orgId:              org.id,
      ref:                values.ref.trim(),
      auditProgrammeYear: values.auditProgrammeYear,
      type:               values.type,
      independence:       values.independence,
      auditMethods:       values.auditMethods,
      plannedDate:        values.plannedDate,
      actualDate:         values.actualDate || null,
      auditor:            values.auditor.trim(),
      scope:              values.scope.trim(),
      criteria:           values.criteria,
      status:             values.status,
      evidenceIds:        values.evidenceIds,
      findings:           (values.findings || []).map((f) => ({
        ...f,
        evidenceIds: Array.isArray(f.evidenceIds) ? f.evidenceIds : [],
      })),
      reportPath:         values.reportPath.trim() || null,
      tags:               values.tags,
      createdAt:          values.createdAt,
      updatedAt:          now,
    };
    audit.changeLog = existingAudit ? appendChangeLog(existingAudit, audit, changedBy) : [];

    try {
      await writeOrgFile(org.id, `audits/${audit.id}.json`, audit);
      setAllAudits(prev => {
        const list = prev || [];
        const idx = list.findIndex(a => a.id === audit.id);
        if (idx >= 0) {
          const updated = [...list];
          updated[idx] = audit;
          return updated;
        }
        return [...list, audit];
      });
      setSaving(false);
      if (editingId === 'new') {
        setEditingId(audit.id);
        setFormMode('edit');
      }
      setDirty(false);
      setSaveRequestTick(0);
      // §4 pdcaStage auto-advance — a newly completed Internal Audit is the
      // Do → Check trigger. Fire after the audit write so the evaluator sees
      // the new record. Best-effort; no in-memory profile sync (App.js owns
      // the org state and will pick up the new stage on next load).
      maybeAdvancePdcaStage(org.id, org).catch(() => {});
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(true);
      }
    } catch {
      if (pendingAppSaveRef.current) {
        pendingAppSaveRef.current = false;
        onSaveResult?.(false);
      }
      setSaving(false);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // ── §11 rule 42 helpers ─────────────────────────────────────────────────
  const renderBlockContent = (hits) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {t.deleteBlockedReferencedBy} {summariseHits(hits, t)}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {hits.map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => { setConfirmState(null); onNavigate?.(HOLDER_NAV[h.holderType], null); }}
            style={{ ...BTN_SECONDARY, ...FONT_LABEL, borderRadius: 6, padding: '0.35rem 0.6rem', textAlign: 'left', cursor: 'pointer' }}
          >
            {`${holderLabel(t, h.holderType, 1)} ${h.holderRef}`}
          </button>
        ))}
      </div>
    </div>
  );

  const renderBulkContent = (allBlocked, deletableCount, blockedCount, total, blockedSummaries) => (
    <div>
      <h3 style={{ ...FONT_BODY, fontWeight: 700, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
        {t.deleteBlockedTitle}
      </h3>
      <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 0.75rem' }}>
        {allBlocked
          ? t.bulkDeleteSummary_allBlocked
          : t.bulkDeleteSummary_mixed
              .replace('{deletable}', String(deletableCount))
              .replace('{blocked}',   String(blockedCount))
              .replace('{total}',     String(total))}
      </p>
      <div style={{ maxHeight: '40vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.25rem' }}>
        {blockedSummaries.map((row, i) => (
          <div key={i} style={{ ...FONT_LABEL, color: TEXT_DEFAULT, padding: '0.3rem 0.5rem', border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, background: BG_SURFACE }}>
            <span style={{ fontWeight: 600 }}>{row.label}</span>
            <span style={{ color: TEXT_MUTED }}>{` — ${t.deleteBlockedReferencedBy} ${row.summary}`}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Delete from form (edit mode) ─────────────────────────────────────────
  const handleDeleteFromForm = async () => {
    if (editingId === null || editingId === 'new') return;
    const audit = (allAudits || []).find(a => a.id === editingId);
    if (!audit) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'audit', audit.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const name = audit.ref || audit.id;
      setConfirmState({
        message: t.deleteConfirm.replace('{name}', name),
        confirmLabel: t.deleteFormBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `audits/${audit.id}.json`);
            setAllAudits(prev => (prev || []).filter(a => a.id !== audit.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(audit.id); return next; });
            closeForm();
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Delete handlers ──────────────────────────────────────────────────────
  const handleDeleteOne = async (audit) => {
    if (scanningDelete) return;
    setScanningDelete(true);
    let hits = [];
    try { hits = await scanReferencesToEntity(org.id, 'audit', audit.id); }
    catch { hits = []; }
    setScanningDelete(false);
    if (hits.length === 0) {
      const name = audit.ref || audit.id;
      setConfirmState({
        message: t.deleteConfirm.replace('{name}', name),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          try {
            await deleteOrgFile(org.id, `audits/${audit.id}.json`);
            setAllAudits(prev => (prev || []).filter(a => a.id !== audit.id));
            setSelectedIds(prev => { const next = new Set(prev); next.delete(audit.id); return next; });
          } catch {
            setSaveState('error');
            setTimeout(() => setSaveState('idle'), 3000);
          }
        },
      });
      return;
    }
    setConfirmState({
      content: renderBlockContent(hits),
      confirmLabel: t.formCancel,
      confirmColor: BTN_SECONDARY,
      onConfirm: () => setConfirmState(null),
    });
  };

  // ── Derived counters ──────────────────────────────────────────────────────
  const allAuditsList = useMemo(() => allAudits || [], [allAudits]);
  const cntPlanned    = allAuditsList.filter(a => a.status === 'Planned').length;
  const cntInProgress = allAuditsList.filter(a => a.status === 'In Progress').length;
  const cntCompleted  = allAuditsList.filter(a => a.status === 'Completed').length;

  // ── Filtered list ────────────────────────────────────────────────────────
  const audits = useMemo(() => {
    return allAuditsList.filter(a => {
      if (filterType   && a.type   !== filterType)   return false;
      if (filterStatus && a.status !== filterStatus) return false;
      if (filterFindings.length > 0) {
        const findings = Array.isArray(a.findings) ? a.findings : [];
        const hasNoFindings = findings.length === 0;
        const wantNone = filterFindings.includes('__none__');
        const wantTypes = filterFindings.filter(x => x !== '__none__');
        const typeMatch = wantTypes.some(ft => findings.some(f => f.type === ft));
        if (!( (wantNone && hasNoFindings) || typeMatch )) return false;
      }
      return true;
    });
  }, [allAuditsList, filterType, filterStatus, filterFindings]);

  // ── Selection ────────────────────────────────────────────────────────────
  const selectedInView = audits.filter(a => selectedIds.has(a.id));
  const allChecked     = audits.length > 0 && selectedInView.length === audits.length;
  const someChecked    = selectedInView.length > 0;
  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(audits.map(a => a.id)));
    else         setSelectedIds(new Set());
  };
  const toggleOne = (id, checked) => {
    setSelectedIds(prev => { const next = new Set(prev); if (checked) next.add(id); else next.delete(id); return next; });
  };
  const handleDeleteSelected = async () => {
    if (!selectedInView.length) return;
    if (scanningDelete) return;
    setScanningDelete(true);
    const items = [...selectedInView];
    let hitsLists;
    try {
      hitsLists = await Promise.all(items.map(a => scanReferencesToEntity(org.id, 'audit', a.id)));
    } catch {
      hitsLists = items.map(() => []);
    }
    setScanningDelete(false);
    const deletable = [];
    const blocked   = [];
    items.forEach((a, i) => {
      if ((hitsLists[i] || []).length === 0) deletable.push(a);
      else blocked.push({ record: a, hits: hitsLists[i] });
    });
    const performBulkDelete = async (cohort) => {
      await Promise.all(cohort.map(a => deleteOrgFile(org.id, `audits/${a.id}.json`).catch(() => {})));
      const deleted = new Set(cohort.map(a => a.id));
      setAllAudits(prev => (prev || []).filter(a => !deleted.has(a.id)));
      setSelectedIds(new Set());
    };
    if (blocked.length === 0) {
      const names = items.map(a => a.ref || a.id).join(', ');
      setConfirmState({
        message: t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: async () => {
          setConfirmState(null);
          await performBulkDelete(items);
        },
      });
      return;
    }
    const blockedSummaries = blocked.map(({ record, hits }) => ({
      label: record.ref || record.id,
      summary: summariseHits(hits, t),
    }));
    const allBlocked = deletable.length === 0;
    setConfirmState({
      content: renderBulkContent(allBlocked, deletable.length, blocked.length, items.length, blockedSummaries),
      confirmLabel: allBlocked ? t.formCancel : t.continueWithDeletables,
      confirmColor: allBlocked ? BTN_SECONDARY : BTN_DESTRUCTIVE_FILLED,
      onConfirm: async () => {
        setConfirmState(null);
        if (allBlocked) return;
        await performBulkDelete(deletable);
      },
    });
  };

  // ── Create NC from finding (§11 rule 34 narrow exception) ────────────────
  // The link to a materialised NC may be written from null to a non-null id
  // even on a Completed audit, so this flow goes through the bidirectional
  // adapter primitive (§11 rule 39) and bypasses the handleFormSave guard.
  const handleCreateNcFromFinding = async (auditId, findingIdx) => {
    const proceed = async () => {
      let persistedAudit;
      try {
        persistedAudit = await readOrgFile(org.id, `audits/${auditId}.json`);
      } catch {
        setSaveState('error');
        setTimeout(() => setSaveState('idle'), 3000);
        return;
      }
      const finding = (persistedAudit.findings || [])[findingIdx];
      if (!finding) return;
      if (finding.type !== 'Major NC' && finding.type !== 'Minor NC') return;
      if (finding.nonconformityId != null) return;
      setNcDialog({ auditId, findingIdx, finding, persistedAudit });
      setNcDialogOwner('');
      setNcDialogSaving(false);
    };
    if (dirty) {
      setConfirmState({
        message: t.createNcUnsaved,
        confirmLabel: t.createNcConfirm,
        onConfirm: () => {
          setConfirmState(null);
          proceed();
        },
      });
      return;
    }
    await proceed();
  };

  const handleNcDialogConfirm = async () => {
    if (!ncDialog || !ncDialogOwner.trim()) return;
    setNcDialogSaving(true);
    const ncPrefix = (org.refPrefixes && org.refPrefixes.nonconformity) || 'nco';
    const year = new Date().getFullYear();
    let seq;
    try {
      const items = await listOrgCollection(org.id, 'nonconformities');
      const prefix = `${ncPrefix}-${year}-`;
      let max = 0;
      items.forEach(item => {
        if (item.ref && item.ref.startsWith(prefix)) {
          const num = parseInt(item.ref.slice(prefix.length), 10);
          if (!isNaN(num) && num > max) max = num;
        }
      });
      seq = max + 1;
    } catch {
      const ts = Date.now().toString().slice(-4);
      seq = parseInt(ts, 10);
    }
    const today = todayStr();
    const ncId = `nc-${today.replace(/-/g, '')}-${String(seq).padStart(3, '0')}`;
    const ncRef = `${ncPrefix}-${year}-${String(seq).padStart(3, '0')}`;
    const now = new Date().toISOString();
    const changedBy = org.consultantPersonId || 'system';

    const nc = {
      id:                          ncId,
      ref:                         ncRef,
      orgId:                       org.id,
      norms:                       org.norms || [],
      category:                    ncDialog.finding.type === 'Major NC' ? 'Major' : 'Minor',
      repeatFinding:               false,
      parentNonconformityId:       null,
      source:                      'Internal Audit',
      auditId:                     ncDialog.auditId,
      date:                        ncDialog.persistedAudit.actualDate || today,
      owner:                       ncDialogOwner,
      clauseRefs:                  ncDialog.finding.clauseRefs || [],
      linkedControlIds:            ncDialog.finding.linkedControlIds || [],
      description:                 ncDialog.finding.description || '',
      corrections:                 [],
      containmentDate:             null,
      rootCauses:                  [],
      correctiveActions:           [],
      preventiveActionsConsidered: null,
      effectivenessReview:         null,
      effectivenessDate:           null,
      verifiedBy:                  null,
      linkedIncidentIds:           [],
      status:                      'Open',
      closedDate:                  null,
      createdAt:                   now,
      changeLog: [{
        timestamp:     now,
        field:         'created',
        previousValue: null,
        newValue:      'created from audit finding',
        changedBy,
      }],
    };

    const updatedFindings = (ncDialog.persistedAudit.findings || []).map((f, i) => (
      i === ncDialog.findingIdx ? { ...f, nonconformityId: ncId } : f
    ));
    const updatedAudit = {
      ...ncDialog.persistedAudit,
      findings:  updatedFindings,
      changeLog: [
        ...(Array.isArray(ncDialog.persistedAudit.changeLog) ? ncDialog.persistedAudit.changeLog : []),
        {
          timestamp:     now,
          field:         `findings[${ncDialog.findingIdx}].nonconformityId`,
          previousValue: null,
          newValue:      ncId,
          changedBy,
        },
      ],
      updatedAt: now,
    };

    try {
      await writeBidirectional(
        org.id,
        { path: `nonconformities/${ncId}.json`,           data: nc,           previousData: null },
        { path: `audits/${ncDialog.auditId}.json`,        data: updatedAudit, previousData: ncDialog.persistedAudit },
      );
      setNcDialog(null);
      try {
        const items = await listOrgCollection(org.id, 'audits');
        setAllAudits(items);
      } catch {
        // best-effort reload — list state will catch up on next view mount
      }
    } catch {
      setNcDialogSaving(false);
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // ── Form view (separate screen, hides the list entirely) ─────────────────
  if (editingId !== null && formInitial !== null) {
    return (
      <>
        <AuditForm
          t={t}
          lang={lang}
          mode={formMode}
          auditId={editingId}
          initialValues={formInitial}
          onSave={handleFormSave}
          onCancel={handleCancel}
          onDelete={handleDeleteFromForm}
          org={org}
          saving={saving}
          saveError={saveState === 'error'}
          onFormChange={() => setDirty(true)}
          saveRequestTick={saveRequestTick}
          dirty={dirty}
          onCreateNcFromFinding={(findingIdx) => handleCreateNcFromFinding(editingId, findingIdx)}
        />
        <ConfirmDialog
          open={confirmState !== null}
          message={confirmState?.message}
          content={confirmState?.content}
          onConfirm={confirmState?.onConfirm}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
          cancelLabel={t.formCancel || 'Cancel'}
          confirmColor={confirmState?.confirmColor}
          secondaryAction={confirmState?.secondaryAction}
        />
        {ncDialog && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 34, 53, 0.45)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              background: BG_SURFACE,
              border: `1px solid ${BORDER_DEFAULT}`,
              borderRadius: 10,
              padding: '1.75rem 2rem',
              maxWidth: 480,
              width: '90%',
            }}>
              <h3 style={{
                ...FONT_SECTION_TITLE,
                color: TEXT_HEADING,
                margin: '0 0 1rem',
              }}>
                {t.createNcTitle}
              </h3>
              <p style={{
                ...FONT_BODY,
                color: TEXT_DEFAULT,
                margin: '0 0 1.25rem',
                lineHeight: 1.5,
              }}>
                {ncDialog?.finding.type} — {((ncDialog?.finding.description || '').length > 120
                  ? ((ncDialog?.finding.description || '').slice(0, 120) + '…')
                  : (ncDialog?.finding.description || ''))}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
                <label style={{
                  ...FONT_SMALL,
                  color: TEXT_MUTED,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                }}>
                  {t.createNcOwnerLabel}
                </label>
                <PeopleSelector
                  orgId={org.id}
                  lang={lang}
                  value={ncDialogOwner}
                  onChange={setNcDialogOwner}
                  disabled={ncDialogSaving}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={() => setNcDialog(null)}
                  disabled={ncDialogSaving}
                  style={{
                    ...BTN_SECONDARY,
                    ...FONT_BUTTON,
                    fontWeight: 400,
                    borderRadius: 8,
                    padding: '0.5rem 1.1rem',
                    cursor: ncDialogSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t.createNcCancel}
                </button>
                <button
                  type="button"
                  onClick={handleNcDialogConfirm}
                  disabled={!ncDialogOwner.trim() || ncDialogSaving}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON,
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.5rem 1.1rem',
                    cursor: (!ncDialogOwner.trim() || ncDialogSaving) ? 'not-allowed' : 'pointer',
                    opacity: (!ncDialogOwner.trim() || ncDialogSaving) ? 0.5 : 1,
                  }}
                >
                  {t.createNcConfirm}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        {/* Left: title */}
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: 0,
          }}>
            {t.title}
          </h2>
        </div>

        {/* Right: new button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleNew}
            disabled={editingId !== null}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none',
              borderRadius: 8, padding: '0.45rem 1.1rem', cursor: editingId ? 'not-allowed' : 'pointer',
              opacity: editingId ? 0.5 : 1,
            }}
          >
            {t.newAudit}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
        <SummaryCard value={allAuditsList.length} label={t.cntTotal}      color={TEXT_HEADING} />
        <SummaryCard value={cntPlanned}           label={t.cntPlanned}    color={TEXT_DEFAULT} />
        <SummaryCard value={cntInProgress}        label={t.cntInProgress} color={UNSAVED_AMBER} />
        <SummaryCard value={cntCompleted}         label={t.cntCompleted}  color={BRAND_PRIMARY} />
      </div>

      {/* Filter bar */}
      {allAudits !== null && allAuditsList.length > 0 && editingId === null && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterType}</span>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {AUDIT_TYPES.map(ty => (<option key={ty} value={ty}>{t.typeLabels[ty]}</option>))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterStatus}</span>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ ...FONT_LABEL, background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.3rem 0.5rem', color: TEXT_DEFAULT }}>
              <option value="">{t.filterAll}</option>
              {AUDIT_STATUSES.map(s => (<option key={s} value={s}>{t.statusLabels[s]}</option>))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ ...FONT_SMALL, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.filterFindings}</span>
            <MultiSelectFilter
              options={[...FINDING_TYPES, '__none__']}
              values={filterFindings}
              onChange={setFilterFindings}
              allLabel={t.filterNoSelection}
              nSelectedTpl={t.filterNSelected}
              getLabel={(opt) => opt === '__none__' ? t.filterNoFindings : (t.findingLabels[opt] || opt)}
            />
          </div>
        </div>
      )}

      {/* Loading */}
      {allAudits === null && (
        <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
          {t.loading}
        </p>
      )}

      {/* Empty state */}
      {allAudits !== null && allAuditsList.length === 0 && editingId === null && (
        <div style={{
          textAlign: 'center', padding: '3rem 1.5rem',
          border: `1px dashed ${BORDER_DEFAULT}`, borderRadius: 12,
          background: BG_SURFACE,
        }}>
          <p style={{
            ...FONT_BODY_STRONG,
            color: TEXT_MUTED, margin: '0 0 0.4rem',
          }}>
            {t.emptyTitle}
          </p>
          <p style={{
            ...FONT_LABEL,
            color: TEXT_DISABLED, margin: 0,
          }}>
            {t.emptyHint}
          </p>
        </div>
      )}

      {/* Bulk action bar */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.75rem', marginBottom: '0.6rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY_DARK }}>
            {t.nSelected.replace('{n}', selectedInView.length)}
          </span>
          <button
            onClick={handleDeleteSelected}
            style={{
              ...BTN_DESTRUCTIVE_OUTLINE,
              ...FONT_TINY,
              borderRadius: 5,
              padding: '0.2rem 0.5rem', cursor: 'pointer',
            }}
          >
            {t.deleteSelected}
          </button>
        </div>
      )}

      {/* Audits table */}
      {allAudits !== null && audits.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}`, marginBottom: '1rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...LIST_TH_STYLE, width: 36, padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    title={t.selectAll}
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </th>
                <th style={LIST_TH_STYLE}>{t.colRef}</th>
                <th style={LIST_TH_STYLE}>{t.colType}</th>
                <th style={LIST_TH_STYLE}>{t.colStatus}</th>
                <th style={LIST_TH_STYLE}>{t.colAuditor}</th>
                <th style={LIST_TH_STYLE}>{t.colPlannedDate}</th>
                <th style={LIST_TH_STYLE}>{t.colFindings}</th>
                <th style={LIST_TH_STYLE}>{t.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {audits.map(audit => (
                <AuditRow
                  key={audit.id}
                  audit={audit}
                  lang={lang}
                  onEdit={handleEdit}
                  onView={handleView}
                  onDelete={handleDeleteOne}
                  checked={selectedIds.has(audit.id)}
                  onToggle={toggleOne}
                  people={people}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        content={confirmState?.content}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || t.deleteBtn || 'Delete'}
        cancelLabel={t.formCancel || 'Cancel'}
        confirmColor={confirmState?.confirmColor}
        secondaryAction={confirmState?.secondaryAction}
      />
    </div>
  );
}
